import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useTranslation } from "react-i18next";
import InfoDisplay from '../components/PatientView/InfoDisplay';
import ClinicSearch from '../components/PatientView/ClinicSearch';
import DisciplineDoctorSelection from '../components/PatientView/DisciplineDoctorSelection';
import TimeSlotSelection from "../components/PatientView/TimeSlotSelection";
import '../styles/PatientView.css';
import { useNavigate } from 'react-router-dom'; 
import moment from 'moment';
// import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import FormatUrl from '../../../utils/UrlFormatter';

// todo - enable the below Recurring component , once we support the functionality

// import RecurringBooking from "../components/PatientView/RecurringBooking";


const mockDisciplines = ['Physiotherapy', 'Massage Therapy', 'Chiropractic', 'Orthopaedics'];

const PatientView = ({logged_in, user}) => {
  const { t } = useTranslation();

  const [csrfToken, setCsrfToken] = useState(''); 
  const [selectedDiscipline, setSelectedDiscipline] = useState('');
  const [availableDoctors, setAvailableDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false); 
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [selectedDay, setSelectedDay] = useState('');
  // const [patientName, setPatientName] = useState('');
  const [patientName, setPatientName] = useState(() => {
    return user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : '';
  });
  const [isAvailableSlotSelected, setIsAvailableSlotSelected] = useState(false);
  const [tooltipMessage, setTooltipMessage] = useState(''); 
  const [uniqueMarkedDays, setUniqueMarkedDays] = useState([]);
  const [filteredSlots, setFilteredSlots] = useState([]);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [searchTerm, setSearchTerm] = useState(''); 
  const [clinics, setClinics] = useState([]);
  const [filteredClinics, setFilteredClinics] = useState([]); 
  const searchContainerRef = useRef(null);
  const [fetchError, setFetchError] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);

  // todo - enable the below Recurring states , once we support the functionality

  // const [isRecurring, setIsRecurring] = useState(false);
  // const [frequency, setFrequency] = useState(1);
  // const [recurrenceType, setRecurrenceType] = useState('weekly');
  // const [recurringStartDate, setRecurringStartDate] = useState('');

  const navigate = useNavigate();

  // Function to fetch the CSRF token
  const fetchCsrfToken = useCallback(async () => {
    try {
      const csrfUrl = FormatUrl(`/csrf-token/`);
      const response = await fetch(csrfUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setCsrfToken(data.csrfToken);
      } else {
        console.error('Failed to fetch CSRF token, status:', response.status);
        setFetchError(`Failed to fetch CSRF token: ${response.status}`);
      }
    } catch (error) {
      console.error('Error in fetchCsrfToken:', error);
      setFetchError(`Failed to fetch CSRF token: ${error.message}`);
    }
  }, []);


  // Function to fetch clinics list
  const fetchClinicsList = useCallback(async () => {
    if (!csrfToken) return;

    try {
        const clinicsUrl = FormatUrl(`/clinics-list/`);
        const response = await fetch(clinicsUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': csrfToken,
            },
            credentials: 'include',
        });

        if (response.ok) {
            const data = await response.json();
            const clinicsData = data.clinics || [];

            setClinics(clinicsData); // Store the full list of clinics
            setFilteredClinics(clinicsData); // Initialize `filteredClinics` to the full list

            // Check if REACT_APP_CLINIC_ID is set and find the matching clinic
            if (process.env.REACT_APP_CLINIC_ID !== "0") {
                const selectedClinic = clinicsData.find(
                    clinic => String(clinic.clinic_id) === String(process.env.REACT_APP_CLINIC_ID)
                );

                if (selectedClinic) {
                    setSelectedClinic({
                        clinic_id: selectedClinic.clinic_id,
                        clinic_name: selectedClinic.clinic_name,
                    });

                    // Update localStorage to persist selection
                    const updatedDetails = {
                        ...JSON.parse(localStorage.getItem('patientBookingDetails') || '{}'),
                        clinicId: selectedClinic.clinic_id,
                        clinicName: selectedClinic.clinic_name,
                    };
                    localStorage.setItem('patientBookingDetails', JSON.stringify(updatedDetails));
                } else {
                    console.error("Clinic ID from .env not found in API response.");
                }
            }
        } else {
            console.error('Failed to fetch clinics list, status:', response.status);
            setFetchError(`Failed to fetch clinics list: ${response.status}`);
        }
    } catch (error) {
        console.error('Error fetching clinics:', error);
        setFetchError(`Failed to fetch clinics: ${error.message}`);
    }
  }, [csrfToken]);

  

  const fetchDoctors = useCallback(async (discipline, clinicId) => {
    // Determine which clinicId to use
    const resolvedClinicId = process.env.REACT_APP_CLINIC_ID !== "0"
        ? process.env.REACT_APP_CLINIC_ID
        : clinicId;

    if (!resolvedClinicId || !discipline) {
        console.error('Clinic ID or discipline is missing.');
        setAvailableDoctors([]);
        const updatedDetails = {
            ...JSON.parse(localStorage.getItem('patientBookingDetails') || '{}'),
            availableDoctors: [],
        };
        localStorage.setItem('patientBookingDetails', JSON.stringify(updatedDetails));
        return;
    }

    setLoadingDoctors(true);
    try {
        // Step 1: Fetch CSRF token
        const csrfUrl = FormatUrl(`/csrf-token/`);
        const csrfResponse = await fetch(csrfUrl, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
        });
        if (!csrfResponse.ok) throw new Error('Failed to fetch CSRF token.');

        const csrfData = await csrfResponse.json();
        const csrfToken = csrfData.csrfToken;

        // Step 2: Fetch doctors using resolvedClinicId
        const apiUrl = FormatUrl(`/get-doctors/?clinic_id=${resolvedClinicId}`);
        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': csrfToken,
            },
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to fetch doctors.');

        const data = await response.json();

        // Step 3: Process the fetched doctors
        const filteredDoctors = (data.practitioners || []).filter(
            (doctor) => doctor.discipline.toLowerCase() === discipline.toLowerCase()
        );

        // Update state and localStorage
        setAvailableDoctors(filteredDoctors);
        const updatedDetails = {
            ...JSON.parse(localStorage.getItem('patientBookingDetails') || '{}'),
            availableDoctors: filteredDoctors,
        };
        localStorage.setItem('patientBookingDetails', JSON.stringify(updatedDetails));

        // Log filtered doctors
        if (filteredDoctors.length === 0) {
            console.warn('No Practitioners found for the selected discipline and clinic.');
        }
    } catch (error) {
        console.error('Error fetching doctors:', error);
        setAvailableDoctors([]); // Reset doctors on error
        const updatedDetails = {
            ...JSON.parse(localStorage.getItem('patientBookingDetails') || '{}'),
            availableDoctors: [],
        };
        localStorage.setItem('patientBookingDetails', JSON.stringify(updatedDetails));
    } finally {
        setLoadingDoctors(false); // Stop the loading indicator
    }
  }, []);

  
  const fetchAvailableSlots = useCallback(async (clinicId, practitionerId) => {
    // Determine the correct clinic ID
    const resolvedClinicId = process.env.REACT_APP_CLINIC_ID !== "0"
        ? process.env.REACT_APP_CLINIC_ID
        : clinicId;

    try {
        const slotsUrl = FormatUrl(`/get-slot/?clinic_id=${resolvedClinicId}&practitioner_id=${practitionerId}`);
        const response = await fetch(slotsUrl, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
        });

        if (response.ok) {
            const data = await response.json();
            return data.slots || [];
        } else {
            console.error('Failed to fetch slots:', response.status);
            return [];
        }
    } catch (error) {
        console.error('Error fetching slots:', error);
        return [];
    }
  }, []);


  useEffect(() => {
    if (user?.first_name && user?.last_name) {
      setPatientName(`${user.first_name} ${user.last_name}`);
    }
  }, [user?.first_name, user?.last_name]);

  // Fetch CSRF token when component mounts
  useEffect(() => {
    fetchCsrfToken();
  }, [fetchCsrfToken]);

  // Fetch clinics list when the csrfToken changes
  useEffect(() => {
    fetchClinicsList();
  }, [csrfToken, fetchClinicsList]);

  useEffect(() => {
    const savedDetails = JSON.parse(localStorage.getItem('patientBookingDetails') || '{}'); // Default to empty object if null
    const actionLog = JSON.parse(localStorage.getItem('actionLog') || '[]'); // Fetch action log to filter booked slots

    // Determine the correct clinic ID
    const resolvedClinicId = process.env.REACT_APP_CLINIC_ID !== "0"
        ? process.env.REACT_APP_CLINIC_ID
        : savedDetails.clinicId;

    // Utility function to filter out booked slots
    const filterBookedSlots = (timeSlots, actionLog) => {
        const bookedSlots = actionLog
            .filter(entry => ['booked', 'approved', 'completed'].includes(entry.status))
            .map(entry => ({
                start: new Date(entry.startTime).getTime(),
                end: new Date(entry.endTime).getTime(),
            }));

        return timeSlots.filter(slot => {
            const slotStart = new Date(slot.start).getTime();
            const slotEnd = new Date(slot.end).getTime();

            // Check if the slot overlaps with any booked slots
            return !bookedSlots.some(
                bookedSlot =>
                    (slotStart >= bookedSlot.start && slotStart < bookedSlot.end) || // Start overlaps
                    (slotEnd > bookedSlot.start && slotEnd <= bookedSlot.end) ||    // End overlaps
                    (slotStart <= bookedSlot.start && slotEnd >= bookedSlot.end)    // Enveloping overlap
            );
        });
    };

    if (savedDetails) {
        setSelectedDiscipline(savedDetails.selectedDiscipline || '');
        setSelectedDoctor(savedDetails.selectedDoctor || '');
        setSelectedSlot(savedDetails.selectedSlot || null);
        setPatientName(
            (user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : '') ||
            savedDetails.patientName
        );

        // Filter available time slots based on booked slots
        const filteredTimeSlots = filterBookedSlots(savedDetails.availableTimeSlots || [], actionLog);
        setAvailableTimeSlots(filteredTimeSlots);

        // Highlight the selected slot if available
        if (filteredTimeSlots && savedDetails.selectedSlot) {
            const updatedSlots = filteredTimeSlots.map(slot => ({
                ...slot,
                isSelected:
                    slot.start === savedDetails.selectedSlot.start &&
                    slot.end === savedDetails.selectedSlot.end,
            }));
            setFilteredSlots(updatedSlots);
        }

        // Update uniqueMarkedDays for the slider
        if (filteredTimeSlots) {
            const uniqueDays = [...new Set(
                filteredTimeSlots.map(slot => moment(slot.start).format('YYYY-MM-DD'))
            )];
            setUniqueMarkedDays(uniqueDays);
        }

        if (savedDetails.selectedSlot) {
            const selectedDay = moment(savedDetails.selectedSlot.start).format('YYYY-MM-DD');
            setSelectedDay(selectedDay);

            // Step 1: Filter slots for the selected day
            const dayFilteredSlots = savedDetails.availableTimeSlots.filter(slot =>
                moment(slot.start).format('YYYY-MM-DD') === selectedDay
            );

            // Step 2: Exclude booked slots for the selected day
            const bookedSlotsForDay = actionLog
                .filter(log =>
                    ['booked', 'approved', 'completed'].includes(log.status) &&
                    moment(log.startTime).format('YYYY-MM-DD') === selectedDay
                )
                .map(log => ({
                    start: new Date(log.startTime),
                    end: new Date(log.endTime),
                }));

            const finalFilteredSlots = dayFilteredSlots.filter(slot =>
                !bookedSlotsForDay.some(
                    booked => booked.start.getTime() === new Date(slot.start).getTime() &&
                              booked.end.getTime() === new Date(slot.end).getTime()
                )
            );

            setFilteredSlots(finalFilteredSlots); // Update filtered slots for the day excluding booked slots
            setIsAvailableSlotSelected(finalFilteredSlots.length > 0); // Ensure Proceed button visibility
        } else {
            setFilteredSlots([]); // Reset filtered slots if no selected day
            setIsAvailableSlotSelected(false); // Reset Proceed button visibility
        }

        if (resolvedClinicId && savedDetails.clinicName) {
            setSelectedClinic({
                clinic_id: resolvedClinicId,
                clinic_name: savedDetails.clinicName,
            });

            if (savedDetails.selectedDiscipline) {
                if (Array.isArray(savedDetails.availableDoctors) && savedDetails.availableDoctors.length > 0) {
                    setAvailableDoctors(savedDetails.availableDoctors);
                } else {
                    fetchDoctors(savedDetails.selectedDiscipline, resolvedClinicId);
                }
            } else {
                setAvailableDoctors([]);
            }
        } else {
            setSelectedClinic(null);
            setAvailableDoctors([]);
        }
    } else {
        setSelectedDiscipline('');
        setSelectedDoctor('');
        setSelectedSlot(null);
        setPatientName('');
        setAvailableTimeSlots([]);
        setFilteredSlots([]); // Reset filtered slots if no data
        setUniqueMarkedDays([]); // Reset marked days for slider
        setSelectedDay(''); // Reset selected day
        setSelectedClinic(null);
        setAvailableDoctors([]);
    }
  }, [fetchDoctors, user?.first_name, user?.last_name]);


  useEffect(() => {
    if (!selectedDay) {
        setBookedSlots([]); 
        return;
    }

    try {
        // Safely retrieve and parse actionLog
        const savedLog = JSON.parse(localStorage.getItem('actionLog') || '[]');

        if (!Array.isArray(savedLog)) {
            console.error("Invalid actionLog format in localStorage.");
            setBookedSlots([]);
            return;
        }

        // Filter booked slots for the selected day
        const bookedSlotsForDay = savedLog
            .filter((entry) =>
                entry.startTime &&
                moment(entry.startTime, moment.ISO_8601, true).isValid() &&
                moment(entry.startTime).format('YYYY-MM-DD') === selectedDay &&
                entry.status === 'booked'
            )
            .map((entry) => ({
                start: new Date(entry.startTime),
                end: new Date(entry.endTime),
                status: entry.status,
            }));

        setBookedSlots(bookedSlotsForDay);
    } catch (error) {
        console.error("Error processing booked slots:", error);
        setBookedSlots([]); // Fallback to an empty array if parsing fails
    }
  }, [selectedDay]);


  useEffect(() => {
    const handleSlotsLogic = async () => {
        const savedDetails = JSON.parse(localStorage.getItem('patientBookingDetails') || '{}');

        if (!savedDetails) {
            setAvailableTimeSlots([]);
            return;
        }

        // Restore availableTimeSlots from localStorage
        const localStorageSlots = savedDetails.availableTimeSlots || [];
        setAvailableTimeSlots(localStorageSlots);

        if (savedDetails.selectedClinic && savedDetails.selectedDoctorId) {
            // Determine the correct clinic ID
            const resolvedClinicId = process.env.REACT_APP_CLINIC_ID !== "0"
                ? process.env.REACT_APP_CLINIC_ID
                : savedDetails.selectedClinic.clinic_id;

            const practitionerId = savedDetails.selectedDoctorId;

            try {
                const fetchedSlots = await fetchAvailableSlots(resolvedClinicId, practitionerId);

                const today = new Date();

                // Filter for slots with status 'AV' and not in the past
                const availableSlots = fetchedSlots.filter(slot => {
                    const slotEndTime = new Date(slot.end);
                    return slot.status === 'AV' && slotEndTime >= today;
                });

                // Only update state if new data is different from current state
                setAvailableTimeSlots(prevSlots => 
                    JSON.stringify(prevSlots) !== JSON.stringify(availableSlots) ? availableSlots : prevSlots
                );

                // Update uniqueMarkedDays based on filtered available slots
                const uniqueDays = [...new Set(
                    availableSlots.map(slot => moment(slot.start).format('YYYY-MM-DD'))
                )];
                setUniqueMarkedDays(prevDays => 
                    JSON.stringify(prevDays) !== JSON.stringify(uniqueDays) ? uniqueDays : prevDays
                );

                // Update patientBookingDetails in localStorage
                const updatedDetails = {
                    ...savedDetails,
                    availableTimeSlots: availableSlots,
                };
                localStorage.setItem('patientBookingDetails', JSON.stringify(updatedDetails));
            } catch (error) {
                console.error('Error refetching slots in Patient View:', error);
            }
        }
    };

    handleSlotsLogic();
  }, [fetchAvailableSlots]);


  useEffect(() => {
    // Extract patient_id from the current URL
    const currentPatientId = window.location.pathname.split("/").pop(); 

    // If the logged-in patient's ID does not match the URL, redirect to dashboard
    if (currentPatientId !== String(user?.patient_id)) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const handleDisciplineChange = async (event) => {
    const discipline = event.target.value;
    setSelectedDiscipline(discipline);
    setAvailableDoctors([]); 
    setSelectedDoctor('');
    setAvailableTimeSlots([]);

    // Determine the correct clinic ID
    const resolvedClinicId = process.env.REACT_APP_CLINIC_ID !== "0"
        ? process.env.REACT_APP_CLINIC_ID
        : selectedClinic?.clinic_id;

    if (discipline && resolvedClinicId) {
        // Fetch doctors filtered by clinic and discipline
        const doctors = await fetchDoctors(discipline, resolvedClinicId);

        // Update localStorage after fetching doctors and updating state
        const updatedDetails = {
            ...JSON.parse(localStorage.getItem('patientBookingDetails') || '{}'),
            selectedDiscipline: discipline,
            availableDoctors: doctors, 
        };
        localStorage.setItem('patientBookingDetails', JSON.stringify(updatedDetails));
    } else {
        console.error('Please select a valid clinic and discipline.');
    }
  };


  const handleDoctorChange = async (event) => {
    const doctorName = event.target.value;
    setSelectedDoctor(doctorName);

    const selectedPractitioner = availableDoctors.find(
        (doctor) => `${doctor.firstName} ${doctor.lastName}` === doctorName
    );

    if (!selectedPractitioner) {
        console.error('Selected practitioner not found in availableDoctors list. Check doctorName:', doctorName);
        console.error('Available Doctors:', availableDoctors);
        return;
    }

    const practitionerId = selectedPractitioner.id;

    // Determine the correct clinic ID
    const resolvedClinicId = process.env.REACT_APP_CLINIC_ID !== "0"
        ? process.env.REACT_APP_CLINIC_ID
        : selectedClinic?.clinic_id;

    if (!resolvedClinicId) {
        console.error("[ERROR] No valid clinic ID available for fetching slots.");
        return;
    }

    // Clear the previously selected slot
    setSelectedSlot(null); // Clear state
    setFilteredSlots([]); // Reset filtered slots

    // Update patientBookingDetails in localStorage
    const updatedDetails = {
        ...JSON.parse(localStorage.getItem('patientBookingDetails') || '{}'),
        selectedSlot: null, // Clear selectedSlot
        selectedDoctor: doctorName, // Save doctor name
        selectedDoctorId: practitionerId, // Save doctor ID
    };
    localStorage.setItem('patientBookingDetails', JSON.stringify(updatedDetails));

    // Fetch slots
    try {
        const fetchedSlots = await fetchAvailableSlots(resolvedClinicId, practitionerId);

        // Ensure we rely fully on backend-provided slots (No breaking into 30-minute intervals)
        const originalProcessedSlots = fetchedSlots.map((slot) => ({
            id: slot.id,
            title: slot.title,  
            start: slot.start,   
            end: slot.end,       
            status: slot.status, 
            isPast: moment(slot.end).isBefore(moment()), 
        }));

        // Identify unique days that have available slots
        const uniqueDays = [...new Set(
            originalProcessedSlots
                .filter((slot) => slot.status === 'AV' && !slot.isPast) // Ensure only available slots
                .map((slot) => moment(slot.start).format('YYYY-MM-DD')) // No need to force UTC
        )];

        // Update state
        setAvailableTimeSlots(originalProcessedSlots);
        setUniqueMarkedDays(uniqueDays);
        setSelectedDay('');
        setFilteredSlots([]);
        setSelectedSlot(null);
        setTooltipMessage('');

    } catch (error) {
        console.error("[ERROR] Fetching slots failed:", error);
    }
  };


  const handleDayChange = (day) => {
    if (selectedDay === day) {
        // Reset state when the same day is clicked
        setSelectedDay('');
        setIsAvailableSlotSelected(false);
        setTooltipMessage('');
        setFilteredSlots([]);
        return;
    }

    // Update selected day and slot availability
    setSelectedDay(day);
    setIsAvailableSlotSelected(true);

    // Ensure all slots have a consistent status field
    const verifiedSlots = availableTimeSlots.map((slot) => ({
        ...slot,
        status: slot.status || (slot.title === 'Available' ? 'AV' : slot.status),
    }));

    // Filter slots based on the selected day and status 'AV'
    const filteredSlots = verifiedSlots.filter((slot) =>
        moment(slot.start).format('YYYY-MM-DD') === day &&
        slot.status === 'AV' && // Ensure only available slots
        !slot.isPast // Exclude past slots
    );

    // Only update state if filtered slots have changed
    setFilteredSlots((prevSlots) =>
        JSON.stringify(prevSlots) !== JSON.stringify(filteredSlots) ? filteredSlots : prevSlots
    );
  };

  
  // const handleRecurringToggle = () => {
  //   if (!isRecurring) {
  //     setIsRecurring(true);
  //     setSelectedDay(''); 
  //     setIsAvailableSlotSelected(false); 
  //     setTooltipMessage('Deselect the recurring session to select an available time slot.'); // Set tooltip
  //   } else {
  //     setIsRecurring(false);
  //     setTooltipMessage(''); 
  //   }
  // };


  // Handle slot click to select a slot
  const handleSlotClick = (slot) => {
    if (!slot) return; // Ensure slot is valid before proceeding

    if (selectedSlot && selectedSlot.start === slot.start && selectedSlot.end === slot.end) {
        // Deselect if the same slot is clicked
        setSelectedSlot(null);
        setIsAvailableSlotSelected(false);
        setTooltipMessage('');
    } else {
        // Select a new slot only if it's different from the currently selected one
        setSelectedSlot((prevSlot) => 
            prevSlot?.start !== slot.start || prevSlot?.end !== slot.end ? slot : prevSlot
        );
        setIsAvailableSlotSelected(true);
        setTooltipMessage(t("patient_view_main.click_proceed_to_book"));
    }
  };


  const handleProceedToBook = () => {
    if (
        selectedDiscipline &&
        selectedDoctor &&
        selectedSlot &&
        isAvailableSlotSelected &&
        filteredSlots.length > 0 &&
        patientName
    ) {
        // Determine the correct clinic ID
        const resolvedClinicId = process.env.REACT_APP_CLINIC_ID !== "0"
            ? process.env.REACT_APP_CLINIC_ID
            : selectedClinic?.clinic_id;

        if (!resolvedClinicId) {
            console.error("[ERROR] No valid clinic ID available for booking.");
            return;
        }

        // Ensure patient details are saved with the correct clinic ID
        savePatientDetailsToLocalStorage();

        // Navigate to booking view with saved details
        navigate('/book-appointment', {
            state: JSON.parse(localStorage.getItem('patientBookingDetails')),
        });
    } else {
        alert('Please ensure all fields are filled out before proceeding.');
    }
  };


  // const handleRecurringProceedToBook = () => {
  //   const baseDay = recurringStartDate; // Use const as baseDay is not reassigned
  
  //   if (selectedDiscipline && selectedDoctor && isRecurring && patientName) {
  //     if (!baseDay) {
  //       alert("Please select a start date for your recurring sessions.");
  //       return;
  //     }
  
  //     const sessions = [];
  //     const validBaseDay = moment(baseDay, 'YYYY-MM-DD', true);
  
  //     if (!validBaseDay.isValid()) {
  //       alert('Invalid date selected. Please select a valid date.');
  //       return;
  //     }
  
  //     for (let i = 0; i < frequency; i++) {
  //       const dateOffset = recurrenceType === 'weekly' ? i * 7 : i * 14;
  //       const newDate = validBaseDay.clone().add(dateOffset, 'days').format('YYYY-MM-DD');
  //       const defaultStartTime = moment(newDate).set({ hour: 9, minute: 0 }).format('YYYY-MM-DD HH:mm');
  //       const defaultEndTime = moment(newDate).set({ hour: 10, minute: 0 }).format('YYYY-MM-DD HH:mm');
  
  //       // Add each session to the sessions array
  //       sessions.push({
  //         date: newDate,
  //         start: defaultStartTime,
  //         end: defaultEndTime,
  //         title: 'Recurring Slot',
  //       });
  //     }
  
  //     const bookingDetails = {
  //       selectedDiscipline,
  //       selectedDoctor,
  //       recurringSessions: sessions,
  //       patientName,
  //       clinicId: selectedClinic.id, // Pass selected clinic ID
  //       clinicName: selectedClinic.name // Get clinic name
  //     };
  
  //     navigate('/book-appointment', {
  //       state: bookingDetails,
  //     });
  //   } else {
  //     alert("Please ensure all fields are filled out before proceeding.");
  //   }
  // };
  
  // const filteredSlots = availableTimeSlots.find((slot) => slot.day === selectedDay)?.times || [];

  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 5,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 3,
        }
      }
    ]
  };

  // Handle search input change
  const handleSearchChange = (event) => {
    const searchValue = event.target.value;
    setSearchTerm(searchValue);


    // Only show the dropdown if there's a search term
    if (searchValue) {
      // Filter from the full clinics list, not the filtered list, for accurate results
      const filteredResults = clinics.filter(clinic =>
        clinic.clinic_name.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredClinics(filteredResults);
    } else {
      // If search is cleared, show the full clinics list again
      setFilteredClinics(clinics);
    }
    
    // Reset selected clinic when search term changes
    setSelectedClinic(null);
    setIsAvailableSlotSelected(false); // Reset available slot selection
    setTooltipMessage(''); // Reset tooltip message when clinic is deselected or search changes

    // Clear clinic-related fields in localStorage
    const updatedDetails = {
      ...JSON.parse(localStorage.getItem('patientBookingDetails') || '{}'),
      clinicId: null,
      clinicName: null,
      selectedDiscipline: '',
      selectedDoctor: '',
      selectedSlot: null,
    };
    localStorage.setItem('patientBookingDetails', JSON.stringify(updatedDetails));
   
  };


  // Define handleClinicSelect to store the selected clinic in state
  const handleClinicSelect = (clinic) => {
    // Determine the correct clinic ID
    const resolvedClinicId = process.env.REACT_APP_CLINIC_ID !== "0"
        ? process.env.REACT_APP_CLINIC_ID
        : clinic.clinic_id;

    // Update state with the selected clinic
    setSelectedClinic({ clinic_id: resolvedClinicId, clinic_name: clinic.clinic_name });
    setSearchTerm(clinic.clinic_name);
    setFilteredClinics([]);
    setTooltipMessage('');
    setAvailableDoctors([]); 
    setSelectedDoctor('');
    setAvailableTimeSlots([]);
    setSelectedDiscipline('');

    // Update the patientBookingDetails key in localStorage
    const updatedDetails = {
        ...JSON.parse(localStorage.getItem('patientBookingDetails') || '{}'),
        clinicId: resolvedClinicId,
        clinicName: clinic.clinic_name,
        selectedDiscipline: '',
        availableDoctors: [], 
        selectedDoctor: '',
        selectedSlot: null,
    };

    localStorage.setItem('patientBookingDetails', JSON.stringify(updatedDetails));
  };


  // Step 2: Detect clicks outside of the search container to close the dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setFilteredClinics([]); // Close the dropdown when clicked outside
      }
    };

    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        setFilteredClinics([]); // Close the dropdown when Escape is pressed
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [searchContainerRef]);


  useEffect(() => {
    if (logged_in && user?.first_name && user?.last_name) {
      const fullName = `${user.first_name} ${user.last_name}`.trim();
      setPatientName(fullName); 
    }
  }, [logged_in, user]); 

  // Temporarily saving Details in localStorage
  const savePatientDetailsToLocalStorage = () => {
    const selectedDoctorId = availableDoctors.find(
        (doctor) => `${doctor.firstName} ${doctor.lastName}` === selectedDoctor
    )?.id || null;

    // Determine the correct clinic ID
    const resolvedClinicId = process.env.REACT_APP_CLINIC_ID !== "0"
        ? process.env.REACT_APP_CLINIC_ID
        : selectedClinic?.clinic_id;

    const bookingDetails = {
        patientId: user?.patient_id,
        patientName,
        selectedClinic: resolvedClinicId
            ? {
                clinic_id: resolvedClinicId,
                clinic_name: selectedClinic?.clinic_name || '',
            }
            : null,
        selectedDiscipline,
        selectedDoctor,
        selectedDoctorId, // Add selectedDoctorId to booking details
        selectedSlot,
        availableDoctors,
        availableTimeSlots, // Dynamic slots fetched from the backend
        filteredSlots,
        selectedDay,
        uniqueMarkedDays,
    };

    // Retrieve existing details from localStorage to avoid unnecessary updates
    const existingDetails = JSON.parse(localStorage.getItem('patientBookingDetails') || '{}');

    if (JSON.stringify(existingDetails) !== JSON.stringify(bookingDetails)) {
        localStorage.setItem('patientBookingDetails', JSON.stringify(bookingDetails));
    }
  };


  const loadPatientDetailsFromLocalStorage = useCallback(() => {
    const savedDetails = JSON.parse(localStorage.getItem('patientBookingDetails') || '{}');

    if (!savedDetails || Object.keys(savedDetails).length === 0) return;

    // Determine the correct clinic ID
    const resolvedClinicId = process.env.REACT_APP_CLINIC_ID !== "0"
        ? process.env.REACT_APP_CLINIC_ID
        : savedDetails.selectedClinic?.clinic_id;

    setPatientName(
        savedDetails.patientName || 
        (user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : '')
    );

    setSelectedClinic(
        resolvedClinicId
            ? {
                clinic_id: resolvedClinicId,
                clinic_name: savedDetails.selectedClinic?.clinic_name || '',
            }
            : null
    );

    setSelectedDiscipline(savedDetails.selectedDiscipline || '');
    setSelectedDoctor(savedDetails.selectedDoctor || '');
    setAvailableDoctors(savedDetails.availableDoctors || []);
    setAvailableTimeSlots(savedDetails.availableTimeSlots || []);
    setFilteredSlots(savedDetails.filteredSlots || []);
    setSelectedDay(savedDetails.selectedDay || '');
    setUniqueMarkedDays(savedDetails.uniqueMarkedDays || []);

  }, [user?.first_name, user?.last_name]);


  useEffect(() => {
    loadPatientDetailsFromLocalStorage();
  }, [loadPatientDetailsFromLocalStorage]);


  return (
    
      <div className="patient-view-layout">
        <div className="sched-patient-view-container">
          {fetchError && <p className="error-message">Error: {fetchError}</p>}
          <h2>{t("patient_view_main.schedule_appointment")}</h2>
          <p className="sched-instructions">{t("patient_view_main.instructions")}</p>

          <div className="sched-patient-name">
            <label>{t("patient_view_main.enter_your_name")}:</label>
            <input
              type="text"
              value={patientName}
              // onChange={handleNameChange}
              disabled
              placeholder={t("patient_view_main.enter_your_name")}
            />
          </div>

          <div className="sched-view-navigation">
              <p className="sched-text">{t("patient_view_main.view_appointments")}:</p>
              <button className="sched-view-button" onClick={() => navigate('/patient-appointments')}>
              {t("patient_view_main.appointments")}
              </button>
          </div>


         {/* Conditionally render ClinicSearch only if REACT_APP_CLINIC_ID is "0" */}
          {process.env.REACT_APP_CLINIC_ID === "0" && (
              <ClinicSearch
                  searchTerm={searchTerm}
                  selectedClinic={selectedClinic}
                  filteredClinics={filteredClinics}
                  handleSearchChange={handleSearchChange}
                  handleClinicSelect={handleClinicSelect}
                  searchContainerRef={searchContainerRef}
              />
          )}

          {selectedClinic && (
            <div className="sched-section-container">
              <DisciplineDoctorSelection
                selectedDiscipline={selectedDiscipline}
                handleDisciplineChange={handleDisciplineChange}
                patientName={patientName}
                mockDisciplines={mockDisciplines}
                selectedClinic={selectedClinic}
                loadingDoctors={loadingDoctors}
                availableDoctors={availableDoctors}
                selectedDoctor={selectedDoctor}
                handleDoctorChange={handleDoctorChange}
              />


              {selectedDoctor ? (
                availableTimeSlots.length > 0 ? (
                  <>
                    <TimeSlotSelection
                      settings={settings}
                      uniqueMarkedDays={uniqueMarkedDays}
                      selectedDay={selectedDay}
                      handleDayChange={handleDayChange}
                      filteredSlots={filteredSlots}
                      selectedSlot={selectedSlot}
                      handleSlotClick={handleSlotClick}
                      bookedSlots={bookedSlots}
                    />
                  </>
                ) : (
                  <>
                    <div className="logout-message-container">
                      <p className="logout-message-title" style={{ fontSize: '0.8rem', fontWeight: "600" }}>
                      {t("patient_view_main.no_available_slots")}
                      </p>
                    </div>
                  </>
                )
              ) : (
                null
              )}

            </div>
          )}
          
          
          {/* Tooltip Message */}
          {tooltipMessage && (
            <div className="custom-tooltip">
            {tooltipMessage}
          </div>
          )}

          {/* {selectedDoctor && selectedClinic && availableTimeSlots.length > 0 && (
              <RecurringBooking
                isRecurring={isRecurring}
                isAvailableSlotSelected={isAvailableSlotSelected}
                frequency={frequency}
                recurrenceType={recurrenceType}
                recurringStartDate={recurringStartDate}
                handleRecurringToggle={handleRecurringToggle}
                setFrequency={setFrequency}
                setRecurrenceType={setRecurrenceType}
                setRecurringStartDate={setRecurringStartDate}
              />
          )} */}

          {selectedSlot &&
          filteredSlots.some(slot => 
            slot.start === selectedSlot.start && slot.end === selectedSlot.end
          ) &&
          patientName && (
            <button 
              style={{ marginTop: '2rem' }} 
              className="sched-proceed-button" 
              onClick={handleProceedToBook}
            >
              {t("patient_view_main.proceed_to_book")}
            </button>
          )}

          {/* {isRecurring && patientName && (
            <button style={{marginTop:'2rem'}} className="sched-proceed-button" onClick={handleRecurringProceedToBook}>
              Book Recurring Sessions
            </button>
          )} */}
        </div>

        <InfoDisplay availableDoctors={availableDoctors} />

      </div>
  
  );
};

export default PatientView;
